import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';

interface Union {
  id: string;
  name: string;
  buildingAddress: string | null;
  city: string | null;
  district: string | null;
  totalUnits: number;
  totalOwners: number;
  monthlyFee: number | string;
  currentBalance: number | string;
  totalCollected: number | string;
  totalExpenses: number | string;
  mullakRegistration: string | null;
  boardChairman: string | null;
  managerId: string | null;
  status: string;
  createdAt: string;
  manager: { id: string; name: string } | null;
  _count?: { units: number; facilityRequests: number };
}

interface Unit {
  id: string;
  unitNumber: string | null;
  unitType: string | null;
  area: number | null;
  ownerName: string | null;
  ownerPhone: string | null;
  monthlyDue: number | string;
  currentBalance: number | string;
}

export function AdminUnionsPage() {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'unions'],
    queryFn: () => api.get<Paginated<Union>>('/api/unions', { query: { limit: 50 } }),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/unions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'unions'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">🏛️ اتحادات الملاك</h1>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ إضافة اتحاد</Button>
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">🏛️</div>
            <div>لا توجد اتحادات بعد</div>
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.data.map((u) => (
            <Card key={u.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="font-black text-navy-dark text-lg">{u.name}</h3>
                    <p className="text-xs text-gray-500">{u.city} {u.district && `· ${u.district}`}</p>
                  </div>
                  <Badge color={u.status === 'active' ? 'green' : u.status === 'suspended' ? 'red' : 'amber'}>
                    {u.status === 'active' ? 'نشط' : u.status === 'suspended' ? 'موقوف' : 'إعداد'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mt-3">
                  <Stat label="وحدات" value={`${u._count?.units ?? 0}/${u.totalUnits}`} />
                  <Stat label="الرصيد" value={`${Number(u.currentBalance).toLocaleString('en-US')}`} />
                  <Stat label="رسوم/شهر" value={`${Number(u.monthlyFee).toLocaleString('en-US')}`} />
                </div>
                {u.boardChairman && (
                  <div className="text-xs text-gray-500 mt-2">رئيس المجلس: <b>{u.boardChairman}</b></div>
                )}
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="primary" className="flex-1" onClick={() => setOpenId(u.id)}>إدارة</Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${u.name}؟`)) deleteM.mutate(u.id); }}>🗑</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openCreate && <UnionEditModal onClose={() => setOpenCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'unions'] })} />}
      {openId && <UnionDetailModal id={openId} onClose={() => setOpenId(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'unions'] })} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">{label}</div><div className="font-bold text-sm">{value}</div></div>;
}

function UnionEditModal({ union, onClose, onSaved }: { union?: Union; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    name: union?.name ?? '',
    buildingAddress: union?.buildingAddress ?? '',
    city: union?.city ?? '',
    district: union?.district ?? '',
    totalUnits: String(union?.totalUnits ?? 1),
    monthlyFee: String(union?.monthlyFee ?? 0),
    mullakRegistration: union?.mullakRegistration ?? '',
    boardChairman: union?.boardChairman ?? '',
    status: union?.status ?? 'setup',
  });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => union
      ? api.put(`/api/admin/unions/${union.id}`, { ...f, totalUnits: Number(f.totalUnits), monthlyFee: Number(f.monthlyFee) })
      : api.post('/api/admin/unions', { ...f, totalUnits: Number(f.totalUnits), monthlyFee: Number(f.monthlyFee) }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{union ? '✏️ تعديل' : '➕ اتحاد جديد'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="اسم الاتحاد *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input label="عدد الوحدات *" type="number" value={f.totalUnits} onChange={(e) => setF({ ...f, totalUnits: e.target.value })} />
          <Input label="المدينة" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <Input label="الحي" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} />
          <div className="md:col-span-2">
            <Input label="عنوان المبنى" value={f.buildingAddress} onChange={(e) => setF({ ...f, buildingAddress: e.target.value })} />
          </div>
          <Input label="رسوم/شهر (ر.س)" type="number" value={f.monthlyFee} onChange={(e) => setF({ ...f, monthlyFee: e.target.value })} />
          <Input label="رقم تسجيل ملاك" value={f.mullakRegistration} onChange={(e) => setF({ ...f, mullakRegistration: e.target.value })} />
          <Input label="رئيس المجلس" value={f.boardChairman} onChange={(e) => setF({ ...f, boardChairman: e.target.value })} />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="setup">إعداد</option>
              <option value="active">نشط</option>
              <option value="suspended">موقوف</option>
            </select>
          </div>
        </div>
        {err && <div className="mx-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.name}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

function UnionDetailModal({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'info' | 'units'>('info');
  const [openEdit, setOpenEdit] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin', 'union', id],
    queryFn: () => api.get<ApiSuccess<Union & { units: Unit[] }>>(`/api/unions/${id}`),
  });

  const u = data?.data;

  const addUnitM = useMutation({
    mutationFn: (unit: Partial<Unit>) => api.post(`/api/admin/unions/${id}/units`, unit),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'union', id] }),
  });
  const deleteUnitM = useMutation({
    mutationFn: (unitId: string) => api.delete(`/api/admin/unions/${id}/units/${unitId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'union', id] }),
  });

  if (!u) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{u.name}</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpenEdit(true)}>✏️</Button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
          </div>
        </div>
        <div className="flex gap-2 px-5 pt-3 border-b">
          <Tab active={tab === 'info'} onClick={() => setTab('info')}>📋 المعلومات</Tab>
          <Tab active={tab === 'units'} onClick={() => setTab('units')}>🏢 الوحدات ({u.units?.length ?? 0})</Tab>
        </div>
        <div className="p-5">
          {tab === 'info' && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="المدينة" value={u.city ?? '—'} />
              <Stat label="الحي" value={u.district ?? '—'} />
              <Stat label="إجمالي الوحدات" value={String(u.totalUnits)} />
              <Stat label="إجمالي الملاك" value={String(u.totalOwners)} />
              <Stat label="الرسوم الشهرية" value={`${Number(u.monthlyFee).toLocaleString('en-US')} ر.س`} />
              <Stat label="الرصيد الحالي" value={`${Number(u.currentBalance).toLocaleString('en-US')} ر.س`} />
              <Stat label="إجمالي المحصّل" value={`${Number(u.totalCollected).toLocaleString('en-US')} ر.س`} />
              <Stat label="إجمالي المصروفات" value={`${Number(u.totalExpenses).toLocaleString('en-US')} ر.س`} />
            </div>
          )}
          {tab === 'units' && (
            <UnitsTab units={u.units ?? []} onAdd={() => addUnitM.mutate({ unitNumber: prompt('رقم الوحدة:') ?? '' })} onDelete={(unitId) => { if (confirm('حذف الوحدة؟')) deleteUnitM.mutate(unitId); }} />
          )}
        </div>
      </div>
      {openEdit && <UnionEditModal union={u} onClose={() => setOpenEdit(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['admin', 'union', id] }); onSaved(); }} />}
    </div>
  );
}

function UnitsTab({ units, onAdd, onDelete }: { units: Unit[]; onAdd: () => void; onDelete: (id: string) => void }) {
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="primary" onClick={onAdd}>+ وحدة</Button>
      </div>
      {units.length === 0 ? (
        <div className="text-center py-8 text-gray-500">لا توجد وحدات</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">رقم</th><th className="text-right px-3 py-2">المالك</th><th className="text-right px-3 py-2">المساحة</th><th className="text-right px-3 py-2">رسوم</th><th className="text-right px-3 py-2">رصيد</th><th></th>
            </tr></thead>
            <tbody>
              {units.map((u) => (
                <tr key={u.id} className="border-b">
                  <td className="px-3 py-2 font-bold">{u.unitNumber || '—'}</td>
                  <td className="px-3 py-2">{u.ownerName || '—'}</td>
                  <td className="px-3 py-2">{u.area ?? '—'}</td>
                  <td className="px-3 py-2">{Number(u.monthlyDue).toLocaleString('en-US')}</td>
                  <td className="px-3 py-2">{Number(u.currentBalance).toLocaleString('en-US')}</td>
                  <td className="px-3 py-2"><button onClick={() => onDelete(u.id)} className="text-red-600 hover:text-red-800">🗑</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}
